import React, { useState } from 'react';

const SignupForm = ({ onSignup, onSwitchToLogin, isLoading = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    ageVerified: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Username validation
    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters long';
    } else if (formData.username.length > 30) {
      newErrors.username = 'Username cannot exceed 30 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, underscores, and hyphens';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // First name validation (optional)
    if (formData.firstName && formData.firstName.length > 50) {
      newErrors.firstName = 'First name cannot exceed 50 characters';
    }

    // Last name validation (optional)
    if (formData.lastName && formData.lastName.length > 50) {
      newErrors.lastName = 'Last name cannot exceed 50 characters';
    }

    // Age verification (required)
    if (!formData.ageVerified) {
      newErrors.ageVerified = 'You must be 18 years or older to use WanderVibe';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('📝 FORM v3: Form submitted, preventDefault called');
    console.log('📝 FORM v3: Event defaultPrevented?', e.defaultPrevented);

    // Prevent double submission
    if (isSubmitting || isLoading) {
      console.log('📝 FORM v3: Blocked - already submitting or loading');
      return;
    }

    console.log('📝 FORM v3: Validating form...');
    if (!validateForm()) {
      console.log('📝 FORM v3: Validation failed');
      return;
    }
    console.log('📝 FORM v3: Validation passed');

    // Clear any previous errors
    setErrors({});
    setIsSubmitting(true);
    console.log('📝 FORM v3: Cleared errors, set submitting state');

    try {
      console.log('📝 FORM v3: Calling onSignup...');
      await onSignup(formData);
      console.log('📝 FORM v3: onSignup completed successfully');
      // Success - the modal will close automatically
    } catch (error) {
      console.error('📝 FORM v3: Signup error caught:', error);
      console.error('📝 FORM v3: Error details - status:', error.status, 'message:', error.message);

      // Handle validation errors from server
      if (error.status === 400 && error.data?.errors) {
        console.log('📝 FORM v3: Handling 400 validation errors');
        // Server returned validation errors array
        const serverErrors = {};
        error.data.errors.forEach(err => {
          const field = err.path || err.param;
          if (field) {
            serverErrors[field] = err.msg || err.message;
          }
        });
        console.log('📝 FORM v3: Setting server errors:', serverErrors);
        setErrors(serverErrors);
      } else if (error.status === 409) {
        console.log('📝 FORM v3: Handling 409 conflict error');
        // Conflict error (email/username already exists)
        if (error.message.includes('email')) {
          console.log('📝 FORM v3: Setting email error');
          setErrors({ email: 'Email address is already registered' });
        } else if (error.message.includes('username')) {
          console.log('📝 FORM v3: Setting username error');
          setErrors({ username: 'Username is already taken' });
        } else {
          console.log('📝 FORM v3: Setting general submit error');
          setErrors({ submit: error.message });
        }
      } else {
        console.log('📝 FORM v3: Handling general error');
        // General signup error
        setErrors({ submit: error.message || 'Registration failed. Please try again.' });
      }

      console.log('📝 FORM v3: Errors have been set, form should now display them');
      console.log('📝 FORM v3: Form will NOT close - staying open to show errors');
    } finally {
      console.log('📝 FORM v3: Resetting submitting state');
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;

    const levels = [
      { level: 0, text: 'Very Weak', color: '#f44336' },
      { level: 1, text: 'Weak', color: '#ff9800' },
      { level: 2, text: 'Fair', color: '#ff9800' },
      { level: 3, text: 'Good', color: '#4caf50' },
      { level: 4, text: 'Strong', color: '#4caf50' },
      { level: 5, text: 'Very Strong', color: '#2e7d32' }
    ];

    return levels[strength];
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="modal-body">
      <div className="modal-header">
        <h2 className="heading-2" style={{ 
          textAlign: 'center',
          marginBottom: 'var(--spacing-2)',
          background: 'var(--gradient-adventure)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Join WanderVibe
        </h2>
        <p className="text-secondary" style={{ 
          textAlign: 'center',
          marginBottom: 'var(--spacing-6)'
        }}>
          Create your account to start your safe travel journey
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* General error message */}
        {errors.submit && (
          <div style={{
            padding: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-4)',
            backgroundColor: 'var(--color-error-light)',
            border: '1px solid var(--color-error)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-error)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: '500'
          }}>
            {errors.submit}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label className="form-label">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={isLoading}
              className="form-input"
              placeholder="First name"
            />
            {errors.firstName && (
              <span className="form-error">
                {errors.firstName}
              </span>
            )}
          </div>

          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label className="form-label">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={isLoading}
              className="form-input"
              placeholder="Last name"
            />
            {errors.lastName && (
              <span className="form-error">
                {errors.lastName}
              </span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label required">
            Email Address
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
            required
            autoComplete="email"
            className={`form-input ${errors.email ? 'form-input-error' : ''}`}
            placeholder="Enter your email"
          />
          {errors.email && (
            <span className="form-error" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>⚠️</span> {errors.email}
            </span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label required">
            Username
          </label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            disabled={isLoading}
            required
            autoComplete="username"
            className={`form-input ${errors.username ? 'form-input-error' : ''}`}
            placeholder="Choose a username"
          />
          {errors.username && (
            <span className="form-error" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>⚠️</span> {errors.username}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#333'
          }}>
            Password *
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              required
              autoComplete="new-password"
              className={`form-input ${errors.password ? 'form-input-error' : ''}`}
              style={{ paddingRight: '48px' }}
              placeholder="Create a password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#666'
              }}
            >
              {showPassword ? '👁️' : '🔒'}
            </button>
          </div>
          
          {formData.password && (
            <div style={{
              marginTop: 'var(--spacing-2)',
              padding: 'var(--spacing-2)',
              backgroundColor: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-base)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--spacing-1)'
              }}>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Password Strength:</span>
                <span style={{ 
                  fontSize: 'var(--font-size-xs)', 
                  color: passwordStrength.color,
                  fontWeight: 'var(--font-weight-semibold)'
                }}>
                  {passwordStrength.text}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'var(--color-border-light)',
                borderRadius: 'var(--radius-sm)',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${(passwordStrength.level / 5) * 100}%`,
                  height: '100%',
                  backgroundColor: passwordStrength.color,
                  transition: 'all var(--transition-base)'
                }}></div>
              </div>
            </div>
          )}
          
          {errors.password && (
            <p style={{
              color: '#f44336',
              fontSize: '14px',
              margin: '4px 0 0 0'
            }}>
              {errors.password}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#333'
          }}>
            Confirm Password *
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
              required
              autoComplete="new-password"
              className={`form-input ${errors.confirmPassword ? 'form-input-error' : ''}`}
              style={{ paddingRight: '48px' }}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={isLoading}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#666'
              }}
            >
              {showConfirmPassword ? '👁️' : '🔒'}
            </button>
          </div>
          {errors.confirmPassword && (
            <p style={{
              color: '#f44336',
              fontSize: '14px',
              margin: '4px 0 0 0'
            }}>
              {errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Age Verification Checkbox */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            cursor: 'pointer',
            gap: '8px'
          }}>
            <input
              type="checkbox"
              name="ageVerified"
              checked={formData.ageVerified}
              onChange={handleChange}
              disabled={isLoading}
              style={{
                marginTop: '4px',
                cursor: 'pointer',
                width: '18px',
                height: '18px',
                accentColor: 'var(--color-teal-500)'
              }}
            />
            <span style={{
              fontSize: '14px',
              color: '#333',
              lineHeight: '1.5'
            }}>
              I confirm that I am 18 years of age or older and agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--color-teal-500)',
                  textDecoration: 'underline'
                }}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--color-teal-500)',
                  textDecoration: 'underline'
                }}
              >
                Privacy Policy
              </a>
            </span>
          </label>
          {errors.ageVerified && (
            <p style={{
              color: '#f44336',
              fontSize: '14px',
              margin: '4px 0 0 26px'
            }}>
              {errors.ageVerified}
            </p>
          )}
        </div>

        {errors.submit && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            ⚠️ {errors.submit}
          </div>
        )}

        {/* Debug info - remove after fixing */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            fontSize: '12px',
            color: '#666',
            backgroundColor: '#f5f5f5',
            padding: '8px',
            borderRadius: '4px',
            marginBottom: '16px',
            fontFamily: 'monospace'
          }}>
            <div>Loading: {String(isLoading)}</div>
            <div>Submitting: {String(isSubmitting)}</div>
            <div>Errors: {JSON.stringify(errors)}</div>
            <div>API URL: {process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}</div>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || isSubmitting}
          className={`btn btn-primary w-full btn-lg ${(isLoading || isSubmitting) ? 'opacity-60' : ''}`}
          style={{ 
            marginTop: 'var(--spacing-2)',
            background: (isLoading || isSubmitting) ? 'var(--color-gray-400)' : 'var(--gradient-primary)'
          }}
        >
          {(isLoading || isSubmitting) ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: 'var(--spacing-2)' }}>
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2">
                  <animate attributeName="r" values="3;6;3" dur="1s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
              Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      <div style={{
        textAlign: 'center',
        marginTop: 'var(--spacing-6)',
        paddingTop: 'var(--spacing-4)',
        borderTop: `1px solid var(--color-border-light)`
      }}>
        <p className="text-secondary text-small" style={{ marginBottom: 'var(--spacing-2)' }}>
          Already have an account?
        </p>
        <button
          onClick={onSwitchToLogin}
          disabled={isLoading}
          className="btn btn-ghost text-small"
          style={{ 
            color: 'var(--color-primary)',
            textDecoration: 'underline',
            padding: 'var(--spacing-1) var(--spacing-2)'
          }}
        >
          Sign In Instead
        </button>
      </div>
    </div>
  );
};

export default SignupForm;